// src/components/molecules/Header/Header.tsx
import React, { useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Image, IconButton, QuestionIcon, Container, Row, Col, DropdownMenu, InfoIcon,
} from 'shared-portal-component';

import '@/components/molecules/Header/Header.scss';
import { useNavigate } from '@tanstack/react-router';

import Avatar from '@/components/atoms/Avatar/Avatar';
import { useAppDispatch } from '@/store';
import { openModal, openConfirm } from '@/store/slices/ui/UISlice';
import type { RootState } from '@/store/rootReducer';
import type { AdminEntity, AdminRow } from '@/types/administrators.types';
import { logoutThunk } from '@/store/thunks/auth/logoutThunk';
import { fetchOrgProfile } from '@/store/thunks/organization/fetchOrgProfile';
import { selectOrganizationData, selectOrgLoadingProfile } from '@/store/selectors/organization';
import { getSessionToken } from '@/utils/sessionStorage';
import { decodeJWT } from '@/utils/jwtDecoder';

// Signed-in admin (adjust to your store shape)
const selectCurrentAdminEntity = (s: RootState): AdminEntity | undefined =>
  (s as any)?.auth?.user as AdminEntity | undefined;

const toAdminRow = (admin?: AdminEntity): AdminRow | undefined =>
  admin
    ? {
        id: admin.id,
        name: admin.name ?? '',
        email: admin.email ?? '',
        phone: (admin as any).phone ?? '',
        status: 'Active',
      }
    : undefined;

interface LogoProps { imageSrc: string; alt: string; }
interface HeaderProps {
  logo: LogoProps;
  customerName: string;
  customerInitials: string;
  companyName: string;
}

const Header: React.FC<HeaderProps> = ({
  logo, customerName, customerInitials, companyName,
}) => {
  const dispatch = useAppDispatch();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const currentAdmin = useSelector(selectCurrentAdminEntity);
  const currentAdminRow = useMemo(() => toAdminRow(currentAdmin), [currentAdmin]);

  const orgProfile = useSelector(selectOrganizationData);
  const loadingProfile = useSelector(selectOrgLoadingProfile);

  const handleEditAdmin = useCallback(() => {
    setMenuOpen(false)

    // Treat `customerSince` as the presence marker for profile being hydrated
    const profileMissing = !orgProfile?.customerSince || !orgProfile?.name;
    if (profileMissing && !loadingProfile) {
         const token = getSessionToken() ?? '';
            const { payload } = decodeJWT<{ sub: string; organizationId: string }>(token);
            const { sub, organizationId } = payload;
            let id = '';
            if (typeof organizationId === 'string' && organizationId !== '') {
              id = organizationId;
            } else if (typeof sub === 'string' && sub !== '') {
              id = sub;
            }
      void dispatch(fetchOrgProfile({id}));
    }

    if (currentAdmin) {
      dispatch(openModal({ type: 'editAdmin', admin: currentAdmin }));
    } else {
      dispatch(openModal({ type: 'createAdmin' }));
    }
  }, [currentAdmin, dispatch, loadingProfile, orgProfile?.customerSince, orgProfile?.name]);

  const menuItems = useMemo(
    () => [
      { icon: <InfoIcon size="1x" />, label: 'Edit Admin', onClick: handleEditAdmin },
      {
        icon: <InfoIcon size="1x" />,
        label: 'Reset Login Info',
        onClick: () => {
          setMenuOpen(false);
          if (currentAdminRow) {
            dispatch(openConfirm({ context: 'admins', kind: 'forceLoginReset', row: currentAdminRow }));
          }
        },
      },
      {
        icon: <InfoIcon size="1x" />,
        label: 'Logout',
        onClick: async() => {
          setMenuOpen(false);
          await dispatch(logoutThunk());
          navigate({ to: '/login', replace: true });
        },
      },
    ],
    [currentAdminRow, dispatch, handleEditAdmin, navigate]
  );

  return (
    <header className="header">
      <Container fluid>
        <Row spacing="md" align="center" justify="between">
          <Col size="content">
            <Row spacing="md" align="center">
              <Col size="content" className="header__logo">
                <Image src={logo.imageSrc} alt={logo.alt} className="header__logo-img" />
              </Col>
              <Col size="content">
                <span className="header__company-name">{companyName}</span>
                <div className="header__subtitle">PortalSIX Admin Center</div>
              </Col>
            </Row>
          </Col>

          <Col size="content">
            <Row spacing="md" align="center">
              <Col size="content">
                <IconButton onClick={() => window.open('https://support.six3ro.com/portal/en/home', '_blank', 'noopener,noreferrer')} icon={<QuestionIcon size="2x" />} ariaLabel="Help" variant="secondary" size="lg" />
              </Col>
              <Col size="content">
                <div className="header__user-anchor">
                  <button
                    type="button"
                    className="header__initials-btn"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    onClick={() => { setMenuOpen(isOpen => !isOpen); }}
                  >
                    <Avatar initials={customerInitials} size="md" />
                    <DropdownMenu open={menuOpen} onClose={() => { setMenuOpen(false); }} menuItems={menuItems} />
                  </button>
                  <span className="header__customer-name">{customerName}</span>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </header>
  );
};

export default Header;
